import type { SourceRef } from "./sources";

/**
 * Références des hubs « année » d'archives rédigés à la main
 * (src/data/editorial-annees-archives.ts).
 *
 * Chaque millésime dispose d'au moins une référence datée renvoyant au fonds
 * exact consulté, complétée par une source de presse ou institutionnelle.
 */

const ina = (query: string, date: string, title: string): SourceRef => ({
  title,
  publisher: "INA — Institut national de l'audiovisuel",
  url: `https://www.ina.fr/recherche?q=${encodeURIComponent(query)}`,
  date,
});

const france3Corse: SourceRef = {
  title: "Actualité et magazines d'enquête en Corse",
  publisher: "France 3 Corse ViaStella",
  url: "https://france3-regions.franceinfo.fr/corse/",
};

const corseMatin: SourceRef = {
  title: "Archives de presse quotidienne régionale",
  publisher: "Corse-Matin",
  url: "https://www.corsematin.com/",
};

const lemonde: SourceRef = {
  title: "Archives et chroniques consacrées à la Corse",
  publisher: "Le Monde",
  url: "https://www.lemonde.fr/corse/",
};

const assembleeCorse: SourceRef = {
  title: "Sessions, statuts et conférences de l'Assemblée de Corse",
  publisher: "Collectivité de Corse",
  url: "https://www.isula.corsica/assemblea/",
};

const wrc: SourceRef = {
  title: "Palmarès et archives du Tour de Corse (championnat du monde des rallyes)",
  publisher: "FIA / WRC",
  url: "https://www.wrc.com/",
};

export const sourcesAnneesArchives: Record<string, SourceRef[]> = {
  "annee/2009": [
    ina("a filetta", "2009", "A Filetta et les polyphonies corses — fonds audiovisuel"),
    france3Corse,
    corseMatin,
  ],
  "annee/2007": [
    ina("scandola", "2007", "Réserve naturelle de Scandola — archives audiovisuelles"),
    france3Corse,
    corseMatin,
  ],
  "annee/2006": [
    ina("corse attentat 2006", "2006", "Journaux télévisés du 23 avril 2006 consacrés à la Corse"),
    wrc,
    lemonde,
  ],
  "annee/2005": [
    ina("corse 2005", "2005", "Journaux de 20 heures des 29 et 30 septembre 2005"),
    wrc,
    corseMatin,
  ],
  "annee/2004": [
    ina("corse racisme 2004", "2004", "Corse 2004 : archives télévisées de l'actualité insulaire"),
    assembleeCorse,
    lemonde,
  ],
  "annee/2003": [
    ina("sc bastia 2003", "2003", "Saison 2003-2004 du SC Bastia — archives sportives"),
    wrc,
    corseMatin,
  ],
  "annee/2002": [
    ina("terra corsa gatlif", "2002", "Terra Corsa et I Muvrini — fonds audiovisuel"),
    france3Corse,
    lemonde,
  ],
  "annee/2001": [
    ina("francois santoni", "2001", "Journal du 18 août 2001 et archives de l'année"),
    lemonde,
    corseMatin,
  ],
  "annee/1999": [
    ina("paillote corse 1999", "1999", "Journaux d'Antenne 2 des 27 et 28 avril 1999"),
    lemonde,
    corseMatin,
  ],
  "annee/1997": [
    ina("corse 1997", "1997", "Corse 1997 : magazines de découverte et actualité"),
    wrc,
    france3Corse,
  ],
  "annee/1996": [
    ina("attentats corse 1996", "1996", "Journal de France 2 du 27 octobre 1996"),
    corseMatin,
    france3Corse,
  ],
  "annee/1995": [
    ina("canta u populu corsu", "1995", "La Nave Va et le répertoire du Riacquistu"),
    france3Corse,
    corseMatin,
  ],
  "annee/1994": [
    ina("incendies corse 1994", "1994", "Journal de France 2 du 10 août 1994 : incendies de forêt"),
    lemonde,
    corseMatin,
  ],
  "annee/1993": [
    ina("liberation corse 1943", "1993", "Cinquantenaire de la Libération de la Corse — FR3 Corse"),
    france3Corse,
    wrc,
  ],
  "annee/1991": [
    ina("chjami aghjalesi", "1991", "Chjami Aghjalesi — captations de concerts"),
    france3Corse,
    corseMatin,
  ],
  "annee/1990": [
    ina("corse attentats septembre 1990", "1990", "Journaux 19/20 des 8 et 12 septembre 1990"),
    france3Corse,
    corseMatin,
  ],
  "annee/1989": [
    ina("corse greve generale 1989", "1989", "Journaux d'Antenne 2 du 16 au 24 mars 1989"),
    lemonde,
    corseMatin,
  ],
  "annee/1988": [
    ina("corse 1988 caserne", "1988", "Journal d'Antenne 2 du 22 octobre 1988"),
    france3Corse,
    corseMatin,
  ],
  "annee/1987": [
    ina("tour de corse 1987", "1987", "Tour de Corse 1987 — archives du rallye"),
    wrc,
    corseMatin,
  ],
  "annee/1986": [
    ina("tour de corse 1986", "1986", "Tour de Corse 1986 et fin des voitures du Groupe B"),
    wrc,
    france3Corse,
  ],
  "annee/1985": [
    ina("tour de corse 1985", "1985", "Tour de Corse 1985 — magazines automobiles"),
    wrc,
    corseMatin,
  ],
  "annee/1984": [
    ina("corse attentats decembre 1984", "1984", "Journal d'Antenne 2 du 2 décembre 1984"),
    france3Corse,
    lemonde,
  ],
  "annee/1983": [
    ina("edmond simeoni 1983", "1983", "Entretiens et journaux consacrés à la Corse en 1983"),
    france3Corse,
    lemonde,
  ],
  "annee/1982": [
    {
      title: "Statut particulier de la Corse — loi du 2 mars 1982 et textes successifs",
      publisher: "Légifrance",
      url: "https://www.legifrance.gouv.fr/",
      date: "1982",
    },
    assembleeCorse,
    ina("la vie filmee des corses", "1982", "La vie filmée des Corses, 1959-1982"),
  ],
  "annee/1980": [
    ina("tour de corse 1980", "1980", "Tour de Corse 1980 — archives du rallye"),
    lemonde,
    wrc,
  ],
  "annee/1979": [
    ina("flnc 1979", "1979", "Corse 1979 : actions revendiquées et réactions politiques"),
    lemonde,
    corseMatin,
  ],
  "annee/1976": [
    ina("flnc 1976", "1976", "Création du FLNC en 1976 — archives audiovisuelles"),
    lemonde,
    france3Corse,
  ],
  "annee/1974": [
    ina("claude papi bastia", "1974", "Claude Papi et le SC Bastia — archives sportives"),
    france3Corse,
    corseMatin,
  ],
  "annee/1973": [
    ina("corse 1973", "1973", "Corse 1973 : documentaires, chant traditionnel et chemins de fer"),
    france3Corse,
    corseMatin,
  ],
  "annee/1972": [
    ina("boues rouges corse", "1972", "Corse 1972 : rejets en mer et archives d'actualité"),
    lemonde,
    corseMatin,
  ],
};
